import { faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Fab, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { messageSVC } from "message/service"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { IUIConfiguration, IUIConfigurationImages, IUIConfigurationUploadParams } from "../model"
import { getCDNAbsoluteUrl, getDarkOrLightColor, getPixelsFromRems } from "../tools/common"
import { setTempUiConfigUndoRedo } from "../tools/undo-redo"
import { UiConfigUploadPopup } from "./UiConfigUploadPopup"

interface IUiConfigFileUploadProps {
  uiConfig: IUIConfiguration
  params: IUIConfigurationUploadParams
  isOptional?: boolean
}

interface IImagePreviewProps {
  uiConfig: IUIConfiguration
  isDark: boolean
  width: number
  height: number
  url: string | null
  imageKey: keyof IUIConfigurationImages
  isOptional?: boolean
  onClick: () => any
}

export const ImagePreview = React.memo(
  ({ uiConfig, isDark, imageKey, width, height, url, onClick, isOptional }: IImagePreviewProps) => {
    const isEmptyUrl = !url
    return (
      <FlexBox
        gap
        vertical
        ai="start"
        onClick={() => onClick()}
        sx={{
          position: "relative",
          minWidth: "7rem",
          minHeight: "12rem",
          padding: "2rem",
          borderRadius: ".25rem",
          backgroundColor: getDarkOrLightColor(isDark, uiConfig.colors.backgroundPaper)
        }}>
        {isOptional && !isEmptyUrl && (
          <Fab
            color="primary"
            aria-label="no image"
            title={translate("admin.ui-configuration.image.empty.tooltip")}
            size="small"
            sx={{ "position": "absolute", "top": "1rem", "right": "1rem", "& .icon": { height: "1.1rem" } }}
            onClick={event => {
              event.stopPropagation()
              event.preventDefault()
              const newUiConfig = { ...uiConfig, images: { ...uiConfig.images } }
              newUiConfig.images[imageKey][isDark ? "darkUrl" : "lightUrl"] = null
              setTempUiConfigUndoRedo(newUiConfig)
            }}>
            <FontAwesomeIcon icon={faXmark} className="icon" />
          </Fab>
        )}
        <Typography sx={{ color: getDarkOrLightColor(isDark, uiConfig.colors.textPrimary) }}>
          {`${translate(isDark ? "label.dark" : "label.light")} (${getPixelsFromRems(width)}px * ${getPixelsFromRems(
            height
          )}px)`}
        </Typography>
        <FlexBox
          sx={{
            width: `${width}rem`,
            minWidth: `${width}rem`,
            maxWidth: `${width}rem`,
            height: `${height}rem`,
            minHeight: `${height}rem`,
            maxHeight: `${height}rem`,
            cursor: "pointer",
            backgroundImage: !url ? "" : `url('${getCDNAbsoluteUrl(url)}')`,
            backgroundRepeat: "no-repeat",
            backgroundSize: isOptional ? "cover" : "contain",
            backgroundColor: isEmptyUrl ? getDarkOrLightColor(isDark, uiConfig.colors.border) : undefined,
            border: theme => theme.palette.border
          }}>
          {isEmptyUrl && (
            <Typography color="textPrimary" fontSize=".9rem">
              {translate("admin.ui-configuration.image.empty")}
            </Typography>
          )}
        </FlexBox>
      </FlexBox>
    )
  }
)

export const UiConfigFileUpload = React.memo(
  ({
    params: { title, imageKey, imageWidthInRem, imageHeightInRem },
    uiConfig,
    isOptional
  }: IUiConfigFileUploadProps) => {
    const [isPopupOpened, setIsPopupOpened] = React.useState(false)
    const [isDark, setIsDark] = React.useState(false)
    const lightUrl = uiConfig.images[imageKey].lightUrl
    const darkUrl = uiConfig.images[imageKey].darkUrl
    return (
      <FlexBox vertical>
        <FlexBox vertical gap ai="start">
          <Typography color="textPrimary">{title}</Typography>
          <ImagePreview
            uiConfig={uiConfig}
            isDark={false}
            height={imageHeightInRem}
            width={imageWidthInRem}
            url={lightUrl}
            isOptional={isOptional}
            imageKey={imageKey}
            onClick={() => {
              if (isDark) {
                setIsDark(false)
              }
              setIsPopupOpened(true)
            }}
          />
          <ImagePreview
            uiConfig={uiConfig}
            isDark={true}
            height={imageHeightInRem}
            width={imageWidthInRem}
            url={darkUrl}
            isOptional={isOptional}
            imageKey={imageKey}
            onClick={() => {
              if (!isDark) {
                setIsDark(true)
              }
              setIsPopupOpened(true)
            }}
          />
        </FlexBox>
        {isPopupOpened && (
          <UiConfigUploadPopup
            title={translate(`admin.ui-config.upload.${imageKey}.${isDark ? "dark" : "light"}`)}
            isIco={imageKey === "favicon"}
            onUploadSuccess={async url => {
              try {
                setIsPopupOpened(false)
                const newUiConfig = { ...uiConfig, images: { ...uiConfig.images } }
                newUiConfig.images[imageKey][isDark ? "darkUrl" : "lightUrl"] = url
                setTempUiConfigUndoRedo(newUiConfig)
              } catch (err) {
                setIsPopupOpened(false)
                console.error(err)
                messageSVC.error(translate("label.error.unexpected"))
              }
            }}
            onClose={() => setIsPopupOpened(false)}
          />
        )}
      </FlexBox>
    )
  }
)
